import { Divider, Stack } from "@mui/material";
import { ButtonInternalLink } from "@src/appV2/redesign/components/ButtonInternalLink";
import { CollapsibleBlock, Text } from "@src/appV2/redesign/system/ui-components";
import { Fragment, useState } from "react";

import { getFaqsWhenEligibleToPayTax } from "../utils/getFaqsWhenEligibleToPayTax";
import { getFaqsWhenIneligibleToPayTax } from "../utils/getFaqsWhenIneligibleToPayTax";

interface FaqAnswerSection {
  title?: string;
  message: string;
}
export interface Faq {
  question: string;
  answer: FaqAnswerSection[];
}

function CollapsibleFaq(props: {
  faq: Faq;
  expanded: boolean;
  onChange: (question: string) => void;
  isLast: boolean;
}) {
  const { faq, expanded, onChange, isLast } = props;
  return (
    <CollapsibleBlock
      title={
        <Text semibold variant="body1" sx={{ lineHeight: 1.5, paddingY: 3 }}>
          {faq.question}
        </Text>
      }
      expanded={expanded}
      LinkComponent={ButtonInternalLink}
      onChange={() => {
        onChange(faq.question);
      }}
    >
      <Stack direction="column" spacing={3} sx={{ marginBottom: isLast ? 0 : 3 }}>
        {faq.answer.map((answerSection) => (
          <Stack key={answerSection.message} direction="column" spacing={1}>
            {answerSection.title && (
              <Stack direction="row">
                <Text semibold variant="body2">
                  {answerSection.title}
                </Text>
              </Stack>
            )}
            <Stack direction="row">
              <Text variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {answerSection.message}
              </Text>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </CollapsibleBlock>
  );
}

export function TaxationFaqs(props: {
  title?: string;
  isEligibleToPayTax: boolean;
  formIssuanceThresholdInDollars: number;
  taxationYear: number;
}) {
  const { title, isEligibleToPayTax, formIssuanceThresholdInDollars, taxationYear } = props;
  const [expandedQuestion, setExpandedQuestion] = useState<string>();

  const faqs = isEligibleToPayTax
    ? getFaqsWhenEligibleToPayTax({ formIssuanceThresholdInDollars, taxationYear })
    : getFaqsWhenIneligibleToPayTax({ formIssuanceThresholdInDollars, taxationYear });

  return (
    <Stack direction="column" spacing={3}>
      {title && (
        <Stack direction="row">
          <Text semibold variant="h5">
            {title}
          </Text>
        </Stack>
      )}
      <Stack direction="column" spacing={2}>
        {faqs.map((faq, faqIndex) => (
          <Fragment key={faq.question}>
            <CollapsibleFaq
              faq={faq}
              expanded={expandedQuestion === faq.question}
              isLast={faqIndex === faqs.length - 1}
              onChange={(question) => {
                if (expandedQuestion === question) {
                  setExpandedQuestion(undefined);
                } else {
                  setExpandedQuestion(question);
                }
              }}
            />
            {faqIndex < faqs.length - 1 && <Divider />}
          </Fragment>
        ))}
      </Stack>
    </Stack>
  );
}
